/**
 * engineNormalJson_Merge_Conditional.ts - Conditional block processing for EngineNormalJson
 * Pure ES Module version
 */

import { JsonValue, JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { evaluateConditionWithContext } from './engineNormalJson_Helper.js';
import { tryGetCanonicalKey } from '../../base/engine/htmlJsonMergeUtil.js';
import type { EngineNormalJson } from './engineNormalJson.js';
import type { TemplateOperation } from '../../../../Assembler/dist/model/templateOperation.js';

import { TemplateOperationKind } from '../../../../Assembler/dist/model/templateOperationKind.js';
/** Parameters for processConditionalBlock */
interface ConditionalBlockParams {
    keyName: string;
    value: JsonObject;
    appSite: string;
    currentTemplateName: string;
    appView: string | null;
    templateKey: string;
    operations: TemplateOperation[];
    jsonData: JsonObject;
    componentHtml: JsonObject | null;
    genComponentHtml: boolean;
    recursionDepth: number;
    contextJson: JsonObject | null;
    flowStack: string[] | null;
    configJson: JsonObject | null;
    parentComponentSink?: TemplateOperation[] | null;
    parentSlotSink?: TemplateOperation[] | null;
    clientMode?: boolean;
}

interface PairedConditionalBlocks {
    positiveContent: JsonObject | null;
    negativeContent: JsonObject | null;
}

export const mergeConditionalMethods = {
    findPairedConditionalBlocks(this: EngineNormalJson, conditionalBlockData: JsonObject): PairedConditionalBlocks {
        const positiveContent = conditionalBlockData.get('Content').asObject();
        const negativeBlock = conditionalBlockData.get('NegativeBlock').asObject();
        const negativeContent = negativeBlock ? negativeBlock.get('Content').asObject() : null;
        return {
            positiveContent,
            negativeContent
        };
    },

    selectConditionalContentForCondition(
        this: EngineNormalJson,
        conditionMet: boolean,
        positiveContent: JsonObject | null,
        negativeContent: JsonObject | null
    ): JsonObject | null {
        if (conditionMet) {
            return positiveContent;
        }

        return negativeContent;
    },

    processConditionalBlock(this: EngineNormalJson, params: ConditionalBlockParams): string {
        const {
            keyName,
            value,
            appSite,
            currentTemplateName,
            appView,
            templateKey,
            operations,
            jsonData,
            componentHtml,
            genComponentHtml,
            recursionDepth,
            contextJson,
            flowStack,
            parentComponentSink = null,
            parentSlotSink = null,
            clientMode = false
        } = params;

        const conditionalBlockData = value;
        let conditionalKey = conditionalBlockData.get('ConditionalKey').asString() || keyName;
        // rawCondition is the key's own truthiness. conditionMet is what selects the rendered body
        // and is inverted for negative conditionals ({{^Key}}). Operation logging must report
        // rawCondition, NOT conditionMet — Normal's handleConditional logs the condition value
        // itself, so reporting the inverted value would flip True/False for every negative block.
        const rawCondition = evaluateConditionWithContext(conditionalKey, contextJson, jsonData);
        let conditionMet = rawCondition;
        const isNegativeVal = conditionalBlockData.get('IsNegative');
        const isNegativeBlock = isNegativeVal instanceof JsonValue
            ? (!isNegativeVal.isNull && (isNegativeVal.asBool() === true || isNegativeVal.asString() === 'true'))
            : (isNegativeVal === true || isNegativeVal === 'true');
        if (isNegativeBlock) {
            conditionMet = !conditionMet;
        }

        // Normal dispatches on the DATA TYPE, not just the marker: a key whose value is an array is
        // owned by the array machinery, which logs nothing for a standalone {{^Key}} (there is no
        // {{@Key}} MainBlock to report). Only non-array values reach its conditional processor and
        // produce a ConditionalBlock op. Mirror that here — rendering is unaffected either way,
        // since array truthiness (non-empty) already drives which body is selected.
        let resolvedConditionalValue: JsonValue | null = null;
        if (conditionalKey) {
            if (contextJson !== null) {
                for (const ctxKey of contextJson.keys()) {
                    if (ctxKey.toLowerCase() === conditionalKey.toLowerCase()) {
                        resolvedConditionalValue = contextJson.get(ctxKey);
                        break;
                    }
                }
            }
            if (resolvedConditionalValue === null && jsonData !== null) {
                for (const dataKey of jsonData.keys()) {
                    if (dataKey.toLowerCase() === conditionalKey.toLowerCase()) {
                        resolvedConditionalValue = jsonData.get(dataKey);
                        break;
                    }
                }
            }
        }
        const ownedByArrayMachinery = isNegativeBlock
            && resolvedConditionalValue instanceof JsonValue
            && resolvedConditionalValue.isArray;

        // Use data-source canonical key casing for Target+ReplacedWith so JSON engines emit
        // the same identifier as Normal/PreProcess (which iterate merged data keys).
        if (conditionalKey) {
            conditionalKey = tryGetCanonicalKey(jsonData, conditionalKey);
        }

        const { positiveContent, negativeContent } = this.findPairedConditionalBlocks(conditionalBlockData);
        const selectedContent = this.selectConditionalContentForCondition(conditionMet, positiveContent, negativeContent);

        // Log the ConditionalBlock op BEFORE merging the body: Normal iterates the merged data keys, so
        // an enclosing conditional is logged ahead of the blocks nested inside it. Logging after the
        // recursion below would emit the parent AFTER its children (post-order) and reorder the ops trace
        // against Normal for any nested conditional (e.g. {{?ShowPasswordReset}} in LoginForm).
        if (operations && !ownedByArrayMachinery) {
            const target = '{{^' + conditionalKey + '}}';
            const alreadyLogged = operations.some(op => op.kind === TemplateOperationKind.ConditionalBlock && op.target === target);
            if (!alreadyLogged) {
                operations.push({
                    templateName: templateKey,
                    kind: TemplateOperationKind.ConditionalBlock,
                    target: target,
                    replacedWith: `${currentTemplateName}_${conditionalKey} (${rawCondition ? 'True' : 'False'})`,
                    operations: [] as TemplateOperation[]
                } as TemplateOperation);
            }
        }

        let result = '';
        if (selectedContent) {
            // Hoist slot/component ops in conditional body to OUTER template's slot/component phase.
            result = this.mergeTemplate(
                selectedContent,
                appSite,
                currentTemplateName,
                appView,
                templateKey,
                operations,
                jsonData,
                componentHtml,
                flowStack,
                genComponentHtml,
                contextJson,
                null,
                recursionDepth + 1,
                parentComponentSink,
                null,
                null,
                parentSlotSink,
                clientMode
            );
        }

        return result;
    }
};
